import React, { useRef, useState, useEffect } from "react";
import { useThree } from "@react-three/fiber";
import { useFrame } from "@react-three/fiber";
import { Text, Billboard } from "@react-three/drei";
import * as THREE from "three";
import { TILE_POSITIONS } from "../../constants/projectsConstants";
import { useAssets } from "../../contexts/AssetContext";
import { useCurrentTile } from "../../contexts/CurrentTileContext";

const ProjectTiles = ({ visible = true }) => {
  const { currentTileIndex } = useCurrentTile();
  
  return (
    <group>
      {TILE_POSITIONS.map((tile, index) => (
        <Tile 
          key={tile.id} 
          tile={tile} 
          tileIndex={index}
          visible={visible}
          isCurrentTile={currentTileIndex === index}
        />
      ))}
    </group>
  );
};

function Tile({ tile, visible = true, tileIndex, isCurrentTile = false }) {
  const matRef = useRef();
  const textRef = useRef();
  const rectRef = useRef();
  const geometryRef = useRef();
  const imageMeshRef = useRef();
  const { camera, size } = useThree();
  const { assets, isLoaded } = useAssets();
  const [isMobile, setIsMobile] = useState(false);
  const [fontSize, setFontSize] = useState(0.6);
  const [previewSize, setPreviewSize] = useState(0.6);
  const [imageSize, setImageSize] = useState(10);
  const [isHovered, setIsHovered] = useState(false);
  const cursorRef = useRef(null);

  // Get preloaded texture from assets context (defined early for use in effects)
  const texture = isLoaded && assets?.projectImages?.[tile.image] 
    ? assets.projectImages[tile.image] 
    : null;

  // Reset cursor immediately when tile is no longer current or becomes invisible
  useEffect(() => {
    if (!visible || !isCurrentTile) {
      // Immediately reset cursor and hover state
      setIsHovered(false);
      document.body.style.cursor = "default";
      // Hide cursor element immediately without transition
      if (cursorRef.current) {
        cursorRef.current.style.transition = "none";
        cursorRef.current.style.opacity = "0";
        // Re-enable transition after reset
        setTimeout(() => {
          if (cursorRef.current) {
            cursorRef.current.style.transition = "opacity 0.1s ease-out";
          }
        }, 0);
      }
    }
  }, [visible, isCurrentTile]);

  // Create and manage custom cursor with project image
  // Only allow hover when this tile is the current tile in view
  useEffect(() => {
    // If tile is no longer current, immediately reset everything and cleanup
    if (!isCurrentTile) {
      setIsHovered(false);
      // Immediately reset cursor without transition
      if (cursorRef.current) {
        cursorRef.current.style.transition = "none";
        cursorRef.current.style.opacity = "0";
        // Re-enable transition after reset
        setTimeout(() => {
          if (cursorRef.current) {
            cursorRef.current.style.transition = "opacity 0.1s ease-out";
          }
        }, 0);
      }
      document.body.style.cursor = "default";
      // Remove event listeners
      return () => {
        document.body.style.cursor = "default";
      };
    }

    // Only proceed if hovered, has texture, and is current tile
    if (!isHovered || !texture || !isCurrentTile) {
      return;
    }

    // Create cursor element if it doesn't exist
    if (!cursorRef.current) {
      const cursor = document.createElement("div");
      cursor.id = `project-cursor-${tile.id}`;
      cursor.style.position = "fixed";
      cursor.style.width = "200px";
      cursor.style.height = "120px";
      cursor.style.pointerEvents = "none";
      cursor.style.zIndex = "9999";
      // cursor.style.borderRadius = "8px";
      cursor.style.overflow = "hidden";
      cursor.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.5)";
      cursor.style.opacity = "0";
      cursor.style.transition = "opacity 0.1s ease-out";
      cursor.style.transform = "translate(-50%, -50%)";
      
      // Create image element
      const img = document.createElement("img");
      // Try to get image source from texture, fallback to tile.image URL
      if (texture.image && texture.image.src) {
        img.src = texture.image.src;
      } else if (texture.image && texture.image instanceof HTMLImageElement) {
        img.src = texture.image.src;
      } else {
        // Fallback to the original image URL
        img.src = tile.image;
      }
      img.style.width = "100%";
      img.style.height = "100%";
      img.style.objectFit = "cover";
      cursor.appendChild(img);
      
      document.body.appendChild(cursor);
      cursorRef.current = cursor;
    }

    // Show cursor
    if (cursorRef.current) {
      cursorRef.current.style.opacity = "1";
      document.body.style.cursor = "none";
    }

    // Update cursor position on mouse move
    const handleMouseMove = (e) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (cursorRef.current) {
        cursorRef.current.style.opacity = "0";
      }
      document.body.style.cursor = "default";
    };
  }, [isHovered, texture, tile.id, tile.image, isCurrentTile]);

  // Cleanup cursor on unmount
  useEffect(() => {
    return () => {
      if (cursorRef.current && cursorRef.current.parentNode) {
        cursorRef.current.parentNode.removeChild(cursorRef.current);
        cursorRef.current = null;
      }
      document.body.style.cursor = "default";
    };
  }, []);

  // Helper function to calculate visible width at a given depth (like ParticleSystem)
  const visibleWidthAtZDepth = (depth, camera) => {
    const cameraOffset = camera.position.z;
    let adjustedDepth = depth;
    if (depth < cameraOffset) adjustedDepth -= cameraOffset;
    else adjustedDepth += cameraOffset;

    const vFOV = (camera.fov * Math.PI) / 180;
    const height = 2 * Math.tan(vFOV / 2) * Math.abs(adjustedDepth);
    return height * camera.aspect;
  };

  // Track viewport width for size calculations
  const cameraDistanceRef = useRef({ width: 0 });

  // Initialize and update sizing on mount and window resize
  useEffect(() => {
    const calculateSizes = () => {
      if (!camera) return;

      setIsMobile(window.innerWidth < 768);

      // Use a fixed reference distance (not based on actual camera position)
      // This keeps tile size consistent regardless of scroll/camera movement
      const referenceZ = -37; // Average of tile z positions
      const fixedReferenceDistance = Math.abs(referenceZ - 25); // Fixed distance from typical camera z (25)

      // Calculate visible width at fixed reference distance
      const visibleWidth = visibleWidthAtZDepth(fixedReferenceDistance, camera);

      // Use different multipliers for mobile vs desktop for better visibility
      const isMobileDevice = window.innerWidth < 768;
      const baseMultiplier = 0.15; // Larger on mobile
      const newPreviewSize = Math.max(
        visibleWidth * baseMultiplier,
        6,
      ); // Minimum size
      const fontSizeMultiplier = isMobileDevice ? 0.02 : 0.01;
      // Clamp font size to prevent it from becoming too large
      const calculatedFontSize = visibleWidth * fontSizeMultiplier;
      const newFontSize = Math.min(Math.max(calculatedFontSize, 0.3), 2.0); // Clamp between 0.3 and 2.0

      // Calculate image size for mobile (show below title)
      const imageSizeMultiplier = isMobileDevice ? 0.2 : 0;
      const newImageSize = isMobileDevice 
        ? Math.max(visibleWidth * imageSizeMultiplier, 5)
        : 0;

      setPreviewSize(newPreviewSize);
      setFontSize(newFontSize);
      setImageSize(newImageSize);
      cameraDistanceRef.current.width = window.innerWidth;
    };

    calculateSizes();
    window.addEventListener("resize", calculateSizes);

    return () => window.removeEventListener("resize", calculateSizes);
  }, [camera]);

  // Create stable shader material uniforms
  const shaderUniforms = useRef({
    time: { value: 0 },
    baseColor: { value: new THREE.Color("#bfbfbf") },
  });

  // Track previous previewSize to only update geometry when it changes
  const prevPreviewSizeRef = useRef(previewSize);

  // Update geometry when previewSize changes
  useEffect(() => {
    if (
      geometryRef.current &&
      rectRef.current &&
      prevPreviewSizeRef.current !== previewSize
    ) {
      const targetWidth = previewSize;
      const targetHeight = previewSize * 0.6;

      // Dispose old geometry
      if (geometryRef.current) {
        geometryRef.current.dispose();
      }

      // Create new geometry with updated size
      geometryRef.current = new THREE.BoxGeometry(targetWidth, targetHeight, 0);
      rectRef.current.geometry = geometryRef.current;

      prevPreviewSizeRef.current = previewSize;
    }
  }, [previewSize]);

  // Create and update image mesh for mobile
  useEffect(() => {
    if (!isMobile || !texture) {
      // Cleanup if not mobile or no texture
      if (imageMeshRef.current) {
        imageMeshRef.current.geometry?.dispose();
        imageMeshRef.current.material?.dispose();
        imageMeshRef.current = null;
      }
      return;
    }

    // Calculate image aspect ratio
    const imageAspect = texture.image 
      ? texture.image.width / texture.image.height 
      : 1;
    const imageWidth = imageSize * imageAspect;
    const imageHeight = imageSize;

    // Create or update image mesh
    if (!imageMeshRef.current) {
      const geometry = new THREE.PlaneGeometry(imageWidth, imageHeight);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        opacity: 1,
      });
      const mesh = new THREE.Mesh(geometry, material);
      imageMeshRef.current = mesh;
    } else {
      // Update existing mesh geometry
      if (imageMeshRef.current.geometry) {
        imageMeshRef.current.geometry.dispose();
      }
      imageMeshRef.current.geometry = new THREE.PlaneGeometry(imageWidth, imageHeight);
    }

    return () => {
      // Cleanup on unmount
      if (imageMeshRef.current) {
        imageMeshRef.current.geometry?.dispose();
        imageMeshRef.current.material?.dispose();
      }
    };
  }, [isMobile, texture, imageSize]);

  useFrame(({ clock }) => {
    if (!camera) return;

    // Update time uniform continuously
    if (shaderUniforms.current) {
      shaderUniforms.current.time.value = clock.elapsedTime;
    }

    // Also update the material ref if it exists (backup)
    if (matRef.current && matRef.current.uniforms) {
      matRef.current.uniforms.time.value = clock.elapsedTime;
    }

    // Sizing is now handled in useEffect on mount and resize
    // No need to update in useFrame, keeping size stable during scroll

    // Text visibility and effects
    if (textRef.current) {
      // Position text centered on screen
      // On mobile: position above center to make room for image below
      // On desktop: center it
      const spacing = isMobile ? fontSize * 0.3 : 0; // Spacing between text and image
      const textYOffset = isMobile ? (imageSize * 0.5 + spacing) : 0;
      textRef.current.position.x = 0; // Center horizontally
      textRef.current.position.y = textYOffset; // Position above center on mobile
      textRef.current.scale.setScalar(1);
      textRef.current.material.opacity = 1;
      textRef.current.visible = true;
    }

    // Image mesh for mobile (positioned below title)
    if (imageMeshRef.current && isMobile && texture) {
      // Position image below the title, centered
      // Center the image vertically below the text
      const spacing = fontSize; // Spacing between text and image
      const imageYOffset = -spacing;
      imageMeshRef.current.position.x = 0; // Center horizontally
      imageMeshRef.current.position.y = imageYOffset; // Position below center
      imageMeshRef.current.position.z = 0.1;
      imageMeshRef.current.visible = isCurrentTile;
    }

    // Rectangle effect
    if (rectRef.current) {
      rectRef.current.visible = true;
      rectRef.current.scale.setScalar(1);
      rectRef.current.rotation.z = 0;
      rectRef.current.position.set(0, 0, 0);
    }
  });

  // Don't render if texture is not loaded yet
  if (!texture) {
    return null;
  }

  return (
    <group position={tile.position}>
      <Billboard>
        {/* ---------- Solid Gray Rectangle ---------- */}
        {/* <mesh
          ref={rectRef}
          key={`rect-${tile.id}`}
          onClick={visible ? () => window.open(tile.link, "_blank") : undefined}
          onPointerOver={
            visible
              ? () => {
                  document.body.style.cursor = "pointer";
                }
              : undefined
          }
          onPointerOut={
            visible
              ? () => {
                  document.body.style.cursor = "default";
                }
              : undefined
          }
        >
          <boxGeometry
            ref={geometryRef}
            args={[previewSize, previewSize * 0.6, 0]}
          />
          <meshBasicMaterial map={texture} />
        </mesh> */}
        {/* ---------- Glossy vibrating text ---------- */}
        <Text
          position={[0, 0, 0]}
          ref={textRef}
          font="/fonts/Azonix.otf"
          fontSize={fontSize}
          anchorX="center"
          anchorY="center"
          textAlign="center"
          // maxWidth={previewSize * 2}
          onPointerOver={
            visible && texture && isCurrentTile
              ? () => {
                  setIsHovered(true);
                }
              : undefined
          }
          onPointerOut={
            visible
              ? () => {
                  setIsHovered(false);
                  document.body.style.cursor = "default";
                }
              : undefined
          }
          onClick={
            visible && tile.link && isCurrentTile
              ? () => window.open(tile.link, "_blank")
              : undefined
          }
        >
          {tile.name}
        </Text>

        {/* Mobile: Project Image below title */}
        {isMobile && imageMeshRef.current && (
          <primitive 
            object={imageMeshRef.current}
            onClick={
              visible && tile.link && isCurrentTile
                ? () => window.open(tile.link, "_blank")
                : undefined
            }
          />
        )}

        {/* <Text
          position={[0, -(previewSize * 0.6) / 2 + 0.2, 0.1]}
          font="/fonts/Azonix.otf"
          fontSize={fontSize * 0.5}
          anchorX="center"
          // anchorY="bottom"
          color="#ffffff"
          maxWidth={previewSize}
        >
          Click to view project
        </Text> */}
      </Billboard>
    </group>
  );
}

export default ProjectTiles;
